import { useCurrentComposition } from "@design-lab/entities/composition";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import type { ArtworkForm } from "@design-lab/shared/types";
import { useLens } from "@hookform/lenses";
import constate from "constate";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

function useArtworkFormCreator() {
  const { currentProduct } = useCurrentComposition();
  const { isEditMode } = useEditMode();

  const form = useForm<ArtworkForm>({
    defaultValues: {
      designProjectUuid: undefined,
      currentPlacement: "front",
      selectedFrontPlacement: "front",
      back: {
        artworkId: "",
        size: {
          height: 0,
          width: 0
        }
      },
      front: {
        artworkId: "",
        size: {
          height: 0,
          width: 0
        }
      },
      frontLeftChest: {
        artworkId: "",
        size: {
          height: 0,
          width: 0
        }
      },
      info: {
        front: {
          enhancementTier: "full_enhancements",
          removeBackground: true,
          jiffyDesignServices: false,
          editImageManual: false,
          enhancementRequestNote: ""
        },
        back: {
          enhancementTier: "full_enhancements",
          removeBackground: true,
          jiffyDesignServices: false,
          editImageManual: false,
          enhancementRequestNote: ""
        }
      }
    },
    mode: "onChange"
  });

  const lens = useLens(form);

  useEffect(() => {
    if (currentProduct && !isEditMode) {
      const { sides } = currentProduct.surfaceMeta || {};
      const currentSelection = form.getValues("selectedFrontPlacement");

      // Skip if user has already made a manual selection (not the default "front")
      if (currentSelection === "frontLeftChest") {
        return;
      }

      // Only set default for new products or when still using default "front"
      const defaultPlacement =
        sides?.front?.includes("front_left_chest") && !sides?.front?.includes("front")
          ? "frontLeftChest"
          : "front";

      if (currentSelection !== defaultPlacement) {
        form.setValue("selectedFrontPlacement", defaultPlacement);
      }
    }
  }, [currentProduct, form, isEditMode]);

  return {
    form,
    lens
  };
}

export const [ArtworkFormProvider, useArtworkForm] = constate(useArtworkFormCreator);
